import type { Observable, Subscription } from 'rxjs'
import {
  EditorPanelShowPropertyReason,
  EntityInteractionEventType,
  isDatumAxis,
  isDatumPlaneMesh,
  isDatumPlaneObject,
  isDatumPoint,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  isSketchObject,
  isSketchPointObject,
  type EntitySelectedEventPayload,
  type SelectionClearedEventPayload,
  type SelectionUpdatedEventPayload,
} from '@puppycad/cad-core'
import type { ViewportSelectionSnapshot } from '@/composables/extrude-types'
import { requestEditorPropertyTab } from '@/views/editor/requestEditorPropertyTab'

//------ 视口选择显示属性面板 开始------
/**
 * 视口空选择来源枚举。
 */
export enum EditorViewportEmptySelectionSource {
  /** 单选或 Ctrl 多选结算为空。 */
  SelectionUpdated = 'selection-updated',
  /** 框选结算为空。 */
  BoxSelectionUpdated = 'box-selection-updated',
}

interface PropertyPanelSelectionRequestPayload {
  /** 当前主选对象 ID。 */
  readonly objectId?: string | null
  /** 当前主选对象。 */
  readonly object?: unknown | null
  /** 当前选择对象 ID 集合。 */
  readonly selectedObjectIds?: readonly string[]
  /** 当前选择对象集合。 */
  readonly selectedObjects?: readonly unknown[]
}
//------ 视口选择显示属性面板 结束------

export interface EditorSelectionSubscriptionViewer {
  /** 监听 cad-core 统一实体交互事件。 */
  observeEntityInteraction?: (eventType: PuppyCAD.EntityInteractionEventType) => Observable<unknown> | null
}

export interface EditorSelectionSubscriptionImportedModelSync {
  /** 同步导入模型选中态到 UI 树。 */
  handleEntitySelected: (payload: EntitySelectedEventPayload) => boolean
  /** 清理导入模型选中态。 */
  handleSelectionCleared: () => boolean
}

export interface RegisterEditorSelectionSubscriptionsOptions {
  /** 当前 Viewer。 */
  viewer: EditorSelectionSubscriptionViewer
  /** 导入模型选择同步器。 */
  importedModelSelectionSync: EditorSelectionSubscriptionImportedModelSync
  /** 视口选择变化处理函数。 */
  handleViewportSelectionChange: (selection: ViewportSelectionSnapshot) => void | Promise<void>
  /** 判断当前是否处于草图选择会话。 */
  isSketchSelectionMode?: () => boolean
  //------ 草图圆角选择属性面板门禁 开始------
  /** 判断当前是否允许视口选择切换到普通草图实体属性面板。 */
  canShowSketchSelectionProperty?: () => boolean
  //------ 草图圆角选择属性面板门禁 结束------
  //------ 草图空选显示设计树 开始------
  /** 视口选择结算为空时的处理函数。 */
  handleViewportEmptySelectionChange?: (source: EditorViewportEmptySelectionSource) => void
  //------ 草图空选显示设计树 结束------
}

/**
 * 注册 Editor 的实体选择事件订阅。
 *
 * 设计说明：
 * 1. 圆角/倒角等命令必须消费 `SelectionUpdated` 提供的稳定快照，不能回退到只含 objectId 的旧事件；
 * 2. `EntitySelected` 与 `SelectionCleared` 仍服务导入模型树同步，但不再驱动特征命令选择流；
 * 3. 订阅集中在该函数内，避免 Editor.vue 中继续散落不同语义事件的拼装逻辑。
 *
 * @param options 注册选项。
 * @returns 已创建的订阅集合。
 */
export function registerEditorSelectionSubscriptions(
  options: RegisterEditorSelectionSubscriptionsOptions,
): Subscription[] {
  const subscriptions: Subscription[] = []
  const entitySelectedObservable = resolveEntityInteractionObservable<EntitySelectedEventPayload>(
    options.viewer,
    EntityInteractionEventType.EntitySelected,
  )
  const selectionClearedObservable = resolveEntityInteractionObservable<SelectionClearedEventPayload>(
    options.viewer,
    EntityInteractionEventType.SelectionCleared,
  )
  //------ 视口选择显示属性面板 开始------
  const entityBoxSelectedObservable = resolveEntityInteractionObservable<PropertyPanelSelectionRequestPayload>(
    options.viewer,
    EntityInteractionEventType.EntityBoxSelected,
  )
  //------ 视口选择显示属性面板 结束------
  const selectionUpdatedObservable = resolveEntityInteractionObservable<SelectionUpdatedEventPayload>(
    options.viewer,
    EntityInteractionEventType.SelectionUpdated,
  )

  const entitySelectedSubscription = entitySelectedObservable?.subscribe((payload) => {
    options.importedModelSelectionSync.handleEntitySelected(payload)
  })
  const selectionClearedSubscription = selectionClearedObservable?.subscribe(() => {
    options.importedModelSelectionSync.handleSelectionCleared()
  })
  const selectionUpdatedSubscription = selectionUpdatedObservable?.subscribe((payload) => {
    //------ 视口选择显示属性面板 开始------
    if (isSettledSelectionEmpty(payload)) {
      options.handleViewportEmptySelectionChange?.(EditorViewportEmptySelectionSource.SelectionUpdated)
    } else {
      requestPropertyPanelForSettledSelection(
        EditorPanelShowPropertyReason.SelectionUpdated,
        payload,
        options,
      )
    }
    //------ 视口选择显示属性面板 结束------
    void options.handleViewportSelectionChange(createViewportSelectionSnapshot(payload))
  })
  //------ 视口选择显示属性面板 开始------
  const entityBoxSelectedSubscription = entityBoxSelectedObservable?.subscribe((payload) => {
    if (isSettledSelectionEmpty(payload)) {
      options.handleViewportEmptySelectionChange?.(EditorViewportEmptySelectionSource.BoxSelectionUpdated)
      return
    }

    requestPropertyPanelForSettledSelection(
      EditorPanelShowPropertyReason.BoxSelectionUpdated,
      payload,
      options,
    )
  })
  //------ 视口选择显示属性面板 结束------

  pushSubscription(subscriptions, entitySelectedSubscription)
  pushSubscription(subscriptions, selectionClearedSubscription)
  pushSubscription(subscriptions, selectionUpdatedSubscription)
  //------ 视口选择显示属性面板 开始------
  pushSubscription(subscriptions, entityBoxSelectedSubscription)
  //------ 视口选择显示属性面板 结束------

  return subscriptions
}

//------ 视口选择显示属性面板 开始------
/**
 * 根据已结算的选择事件请求显示属性面板。
 *
 * @param reason 右侧属性面板显示原因。
 * @param payload 已结算的选择事件载荷。
 */
function requestPropertyPanelForSettledSelection(
  reason: EditorPanelShowPropertyReason,
  payload: PropertyPanelSelectionRequestPayload,
  options: Pick<RegisterEditorSelectionSubscriptionsOptions, 'isSketchSelectionMode' | 'canShowSketchSelectionProperty'>,
): void {
  const selectedObjectCount = resolveSelectedObjectCount(payload)

  // 空白点击或空框选只负责清理选择，不应把右侧面板重新切回属性页。
  if (selectedObjectCount <= 0) {
    return
  }

  if (isFeatureTopologySelection(payload)) {
    return
  }

  if (isDatumReferenceSelection(payload)) {
    return
  }

  if (reason === EditorPanelShowPropertyReason.SelectionUpdated && !isSketchEntitySelectionPropertyEnabled(payload, options)) {
    return
  }

  if (reason === EditorPanelShowPropertyReason.BoxSelectionUpdated && !isSketchBoxSelectionPropertyEnabled(options)) {
    return
  }

  requestEditorPropertyTab(
    reason,
    resolvePrimaryObjectId(payload),
    selectedObjectCount,
  )
}

/**
 * 判断草图实体点选是否允许切换到属性页。
 *
 * 设计说明：
 * 1. 只有正在编辑草图时，点选草图线、圆、圆弧、样条或草图点才表达编辑草图属性的意图；
 * 2. 普通实体、设计树程序化选中和装配对象即使产生 `SelectionUpdated`，也不应打开 PropertyManager；
 * 3. 这里依赖 cad-core 草图对象协议识别对象类型，避免用特征树元信息反向耦合 UI tab 行为。
 *
 * @param payload 已结算的选择事件载荷。
 * @param options 选择订阅配置。
 * @returns 允许草图实体点选切换属性页时返回 true。
 */
function isSketchEntitySelectionPropertyEnabled(
  payload: PropertyPanelSelectionRequestPayload,
  options: Pick<RegisterEditorSelectionSubscriptionsOptions, 'isSketchSelectionMode' | 'canShowSketchSelectionProperty'>,
): boolean {
  return options.isSketchSelectionMode?.() === true
    && options.canShowSketchSelectionProperty?.() !== false
    && containsSketchPropertyTarget(payload)
}

/**
 * 判断当前选择结果中是否包含可编辑草图属性的目标。
 *
 * @param payload 已结算的选择事件载荷。
 * @returns 包含草图实体或草图点时返回 true。
 */
function containsSketchPropertyTarget(payload: PropertyPanelSelectionRequestPayload): boolean {
  const candidates = [
    payload.object,
    ...(payload.selectedObjects ?? []),
  ]

  return candidates.some(isSketchPropertyTargetObject)
}

/**
 * 判断对象是否为可进入草图属性页的草图目标。
 *
 * @param candidate 待判断对象。
 * @returns 草图实体或草图点返回 true。
 */
function isSketchPropertyTargetObject(candidate: unknown): boolean {
  return isSketchObject(candidate) || isSketchPointObject(candidate)
}

/**
 * 判断当前选择是否为特征拓扑点线面。
 *
 * 设计说明：
 * 1. FeaturePoint / FeatureLine / FeatureFace 是创建特征后的拓扑代理；
 * 2. 点击它们仍要保留选择快照，供草图引用、配合、测量等命令消费；
 * 3. 但这类拓扑点击不代表要打开右侧属性页，因此只拦截属性页切换事件。
 *
 * @param payload 已结算的选择事件载荷。
 * @returns 选择对象中包含特征拓扑点线面时返回 true。
 */
function isFeatureTopologySelection(payload: PropertyPanelSelectionRequestPayload): boolean {
  const candidates = [
    payload.object,
    ...(payload.selectedObjects ?? []),
  ]

  return candidates.some((candidate) => (
    isFeaturePoint(candidate)
    || isFeatureLine(candidate)
    || isFeatureFace(candidate)
  ))
}

/**
 * 判断当前选择是否为正式基准点、基准轴或基准面。
 *
 * 设计说明：
 * 1. 用户后续创建的 datum 点、轴、面与默认三基准面一样属于设计/参考对象；
 * 2. 点击它们应只同步选中与高亮，不能强制打开右侧属性页；
 * 3. 订阅层兜底识别视口直接点选，避免只依赖设计树来源元信息。
 *
 * @param payload 已结算的选择事件载荷。
 * @returns 选择对象中包含正式基准对象时返回 true。
 */
function isDatumReferenceSelection(payload: PropertyPanelSelectionRequestPayload): boolean {
  const candidates = [
    payload.object,
    ...(payload.selectedObjects ?? []),
  ]

  return candidates.some(isDatumReferenceObject)
}

/**
 * 判断对象是否为正式基准点、基准轴、基准面根对象或基准面网格对象。
 *
 * @param candidate 待判断对象。
 * @returns 命中正式基准对象时返回 true。
 */
function isDatumReferenceObject(candidate: unknown): boolean {
  if (typeof candidate !== 'object' || candidate === null) {
    return false
  }

  const object = candidate as Parameters<typeof isDatumPlaneObject>[0]
  return isDatumPoint(object)
    || isDatumAxis(object)
    || isDatumPlaneObject(object)
    || isDatumPlaneMesh(object)
}

/**
 * 判断框选命中是否允许切换到属性页。
 *
 * 设计说明：
 * 1. 只有草图会话中的框选才表达“编辑草图实体属性”的意图；
 * 2. 默认态、装配态与其他命令态框选只负责选择与高亮，不应打断右侧当前面板；
 * 3. 未注入模式判断时按非草图处理，避免只读预览等复用入口误切 tab。
 *
 * @param options 选择订阅配置。
 * @returns 允许框选切换属性页时返回 true。
 */
function isSketchBoxSelectionPropertyEnabled(
  options: Pick<RegisterEditorSelectionSubscriptionsOptions, 'isSketchSelectionMode' | 'canShowSketchSelectionProperty'>,
): boolean {
  return options.isSketchSelectionMode?.() === true
    && options.canShowSketchSelectionProperty?.() !== false
}

/**
 * 判断已结算选择是否为空。
 *
 * @param payload 已结算的选择事件载荷。
 * @returns 选择为空时返回 true。
 */
function isSettledSelectionEmpty(payload: PropertyPanelSelectionRequestPayload): boolean {
  return resolveSelectedObjectCount(payload) <= 0
}

/**
 * 解析选择事件中的主对象 ID。
 *
 * @param payload 已结算的选择事件载荷。
 * @returns 主对象 ID；无法解析时返回 null。
 */
function resolvePrimaryObjectId(payload: PropertyPanelSelectionRequestPayload): string | null {
  return payload.objectId ?? payload.selectedObjectIds?.[0] ?? null
}

/**
 * 解析选择事件中的对象数量。
 *
 * @param payload 已结算的选择事件载荷。
 * @returns 当前选择对象数量。
 */
function resolveSelectedObjectCount(payload: PropertyPanelSelectionRequestPayload): number {
  if (payload.selectedObjectIds) {
    return payload.selectedObjectIds.length
  }

  if (payload.selectedObjects) {
    return payload.selectedObjects.length
  }

  return payload.objectId ? 1 : 0
}
//------ 视口选择显示属性面板 结束------

/**
 * 按事件类型读取实体交互 Observable。
 *
 * @param viewer 当前 Viewer。
 * @param eventType 事件类型。
 * @returns 事件流；Viewer 不支持该事件时返回 null。
 */
function resolveEntityInteractionObservable<TPayload>(
  viewer: EditorSelectionSubscriptionViewer,
  eventType: PuppyCAD.EntityInteractionEventType,
): Observable<TPayload> | null {
  return (viewer.observeEntityInteraction?.(eventType) ?? null) as Observable<TPayload> | null
}

/**
 * 将 cad-core 的选择结算事件转换为 web-client 命令宿主快照。
 *
 * @param payload cad-core 选择结算事件载荷。
 * @returns 视口选择快照。
 */
function createViewportSelectionSnapshot(payload: SelectionUpdatedEventPayload): ViewportSelectionSnapshot {
  return {
    objectId: payload.objectId,
    object: payload.object,
    selectedObjects: payload.selectedObjects,
    topologyPick: payload.topologyPick,
    topologyPicks: payload.topologyPicks,
    selectionIntent: payload.selectionIntent,
  }
}

/**
 * 安全收集可用订阅。
 *
 * @param subscriptions 订阅集合。
 * @param subscription 待追加订阅。
 */
function pushSubscription(subscriptions: Subscription[], subscription: Subscription | undefined): void {
  if (subscription) {
    subscriptions.push(subscription)
  }
}
